import React, { useState, useEffect } from 'react';
import BackButton from '@/components/BackButton';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useLanguage } from '@/contexts/LanguageContext';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Plus, Trash, CalendarCheck } from '@phosphor-icons/react';
import { toast } from 'sonner';
import WorkSchedulesSection from './settings/WorkSchedulesSection';
import AppConfigSections from './settings/AppConfigSections';
import DepartmentsSection from './settings/DepartmentsSection';
import DangerZoneSection from './settings/DangerZoneSection';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const LOGO_URL = "/branding/logo.png";

export default function Settings() {
  const { t } = useTranslation();
  const { direction, language } = useLanguage();
  const navigate = useNavigate();

  const [holidays, setHolidays] = useState([]);
  const [showAddDialog, setShowAddDialog] = useState(false);
  const [formData, setFormData] = useState({ name: '', name_en: '', start_date: '', end_date: '' });
  const [loading, setLoading] = useState(true);

  useEffect(() => { fetchData(); }, []);

  const fetchData = async () => {
    try {
      const holidaysRes = await axios.get(`${API}/settings/official-holidays`, { withCredentials: true }).catch(() => null);
      if (holidaysRes) setHolidays(holidaysRes.data);
    } finally {
      setLoading(false);
    }
  };

  const handleAddHoliday = async () => {
    if (!formData.name || !formData.start_date || !formData.end_date) return;
    try {
      const { data } = await axios.post(`${API}/settings/official-holidays`, formData, { withCredentials: true });
      setHolidays(prev => [...prev, data]);
      setShowAddDialog(false);
      setFormData({ name: '', name_en: '', start_date: '', end_date: '' });
      toast.success(t('holiday_added'));
    } catch (err) {
      console.error('add-holiday failed:', err.response?.status, err.response?.data, err.message);
      toast.error(err.response?.data?.detail || err.message || t('error'));
    }
  };

  const handleDeleteHoliday = async (id) => {
    try {
      await axios.delete(`${API}/settings/official-holidays/${id}`, { withCredentials: true });
      setHolidays(prev => prev.filter(h => h.id !== id));
      toast.success(t('holiday_deleted'));
    } catch (err) {
      console.error('delete-holiday failed:', err.response?.status, err.response?.data, err.message);
      toast.error(err.response?.data?.detail || err.message || t('error'));
    }
  };

  const calcDays = (start, end) => {
    if (!start || !end) return 0;
    const s = new Date(start);
    const e = new Date(end);
    return Math.max(0, Math.floor((e - s) / (1000 * 60 * 60 * 24)) + 1);
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#F2F0FA] flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#655dc6]" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#F2F0FA]" dir={direction}>
      <header className="sticky top-0 z-40 bg-white/95 backdrop-blur-sm shadow-sm border-b border-[#C4BFF0] py-4">
        <div className="max-w-[1800px] mx-auto px-4 sm:px-6 lg:px-8 flex items-center justify-between">
          <img src={LOGO_URL} alt="Lucent" className="h-10 object-contain" />
          <BackButton className="text-[#655dc6]" />
        </div>
      </header>

      <main className="max-w-[1800px] mx-auto px-4 sm:px-6 lg:px-8 py-6 space-y-6">
        {/* Row 1: Work Schedules + Departments side-by-side on lg+ */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          <WorkSchedulesSection />
          <DepartmentsSection language={language} />
        </div>

        {/* Unified app config: payroll cycle, calc rules, geofence, company info */}
        <AppConfigSections language={language} direction={direction} />

        {/* Official Holidays */}
        <Card className="border-[#C4BFF0]" data-testid="official-holidays-card">
          <CardHeader>
            <div className="flex items-center justify-between">
              <CardTitle className="text-[#655dc6] flex items-center gap-2">
                <CalendarCheck size={22} weight="duotone" />
                {t('official_holidays')}
              </CardTitle>
              <Button onClick={() => setShowAddDialog(true)} className="bg-[#655dc6] hover:bg-[#544cb0] text-white gap-2" data-testid="add-holiday-btn">
                <Plus size={18} weight="bold" />
                {t('add_holiday')}
              </Button>
            </div>
          </CardHeader>
          <CardContent>
            {holidays.length === 0 ? (
              <p className="text-[#655dc6]/50 text-center py-8">{t('no_holidays')}</p>
            ) : (
              <div className="overflow-x-auto">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead className="text-[#655dc6]">{t('holiday_name')}</TableHead>
                      <TableHead className="text-[#655dc6]">{language === 'en' ? t('holiday_name_en') : ''}</TableHead>
                      <TableHead className="text-[#655dc6]">{t('start_date')}</TableHead>
                      <TableHead className="text-[#655dc6]">{t('end_date')}</TableHead>
                      <TableHead className="text-[#655dc6]">{t('days_count')}</TableHead>
                      <TableHead className="text-[#655dc6]">{t('actions')}</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {holidays.map(h => (
                      <TableRow key={h.id}>
                        <TableCell className="font-medium text-[#655dc6]">{h.name}</TableCell>
                        <TableCell className="text-[#655dc6]/70">{h.name_en || '-'}</TableCell>
                        <TableCell className="text-[#655dc6]/70">{h.start_date}</TableCell>
                        <TableCell className="text-[#655dc6]/70">{h.end_date}</TableCell>
                        <TableCell className="text-[#655dc6]/70">{calcDays(h.start_date, h.end_date)}</TableCell>
                        <TableCell>
                          <Button
                            variant="ghost"
                            size="icon"
                            onClick={() => handleDeleteHoliday(h.id)}
                            className="h-8 w-8 text-red-500 hover:text-red-700 hover:bg-red-50"
                            data-testid={`delete-holiday-${h.id}`}
                          >
                            <Trash size={18} />
                          </Button>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Danger Zone — destructive data wipe */}
        <DangerZoneSection />
      </main>

      {/* Add Holiday Dialog */}
      <Dialog open={showAddDialog} onOpenChange={setShowAddDialog}>
        <DialogContent className="sm:max-w-[450px]" dir={direction}>
          <div dir={direction} style={{ direction }}>
            <DialogHeader>
              <DialogTitle>{t('add_holiday')}</DialogTitle>
              <DialogDescription>{direction === 'rtl' ? 'أضف عطلة رسمية للنظام' : 'Add an official holiday to the system'}</DialogDescription>
            </DialogHeader>
            <div className="space-y-4 mt-4">
              <div className="space-y-2">
                <Label>{t('holiday_name')}</Label>
                <Input value={formData.name} onChange={e => setFormData(p => ({ ...p, name: e.target.value }))} data-testid="holiday-name-input" />
              </div>
              <div className="space-y-2">
                <Label>{t('holiday_name_en')}</Label>
                <Input value={formData.name_en} onChange={e => setFormData(p => ({ ...p, name_en: e.target.value }))} dir="ltr" data-testid="holiday-name-en-input" />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>{t('start_date')}</Label>
                  <Input type="date" value={formData.start_date} onChange={e => setFormData(p => ({ ...p, start_date: e.target.value }))} data-testid="holiday-start-input" />
                </div>
                <div className="space-y-2">
                  <Label>{t('end_date')}</Label>
                  <Input type="date" value={formData.end_date} onChange={e => setFormData(p => ({ ...p, end_date: e.target.value }))} data-testid="holiday-end-input" />
                </div>
              </div>
            </div>
            <DialogFooter className="mt-4">
              <Button onClick={handleAddHoliday} className="bg-[#655dc6] hover:bg-[#544cb0]" data-testid="save-holiday-btn">{t('save')}</Button>
              <Button variant="outline" onClick={() => setShowAddDialog(false)}>{t('cancel')}</Button>
            </DialogFooter>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
